import { Inject, Injectable, OnDestroy } from "@angular/core";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Observable, Subject, Subscription } from "rxjs";
import { API_BASE_URL } from "src/swagger/api.client.generated";
import { NovaLogService } from "./nova-log.service";
import { NovaDeviceService } from "./nova-device.service";
import { NovaRealtimeSessionService } from "./nova-realtime-session.service";
import {
  NovaRealtimeEventRequest,
  NovaRealtimeEventResponse,
  NovaRealtimeFunctions,
  NovaRealtimeRelayExchange,
  NovaRealtimeRelayedEventTypes,
  NovaRealtimeServerEvent,
  NovaRelayedTurn,
} from "./nova-realtime.model";

/**
 * B6. Function calls with no server handler in v1 - see NovaRealtimeFunctions and
 * NovaRealtimeRelayService (server) for why. Answering these over HTTP would be a wasted round
 * trip at best and, if NovaWalkthroughService also answered the same call_id locally, two
 * function_call_output events for one call at worst - so these never reach `relay()` at all;
 * see `onFunctionCall` and `clientHandledFunctionCalls$`.
 */
const CLIENT_HANDLED_FUNCTIONS: ReadonlySet<string> = new Set([
  NovaRealtimeFunctions.NextStep,
  NovaRealtimeFunctions.PauseWalkthrough,
  NovaRealtimeFunctions.ResumeWalkthrough,
  NovaRealtimeFunctions.SkipTo,
  NovaRealtimeFunctions.EndWalkthrough,
]);

/**
 * How many times one event is offered to the server before it is given up on.
 *
 * Three, not more: the server keeps its own consecutive-failure count and terminates the session at
 * `Nova:Realtime:RelayFailureThreshold`, so a client that retried indefinitely would just delay the
 * moment the user is told, while the conversation carried on unrecorded.
 */
const RELAY_ATTEMPTS = 3;

/** Backoff before each retry. One entry per gap, so one fewer than RELAY_ATTEMPTS. */
const RELAY_BACKOFF_MS = [400, 1200];

/**
 * Roughly how many characters of Nova's speech go past in a second.
 *
 * WHY AN ESTIMATE IS NEEDED AT ALL. Over WebRTC the audio arrives on the media track, not the data
 * channel, so there are no `response.audio.delta` frames to measure against - the only text signal
 * is `response.audio_transcript.delta`, and that streams AHEAD of playback because generation
 * outruns speech. Relaying the accumulated deltas as "what was heard" would over-report, sometimes
 * by a multiple, recording a paragraph the user cut off after four words. That is the exact failure
 * this ticket exists to prevent, so the elapsed playback time is used instead.
 *
 * 15 char/s is about 150 words per minute at six characters a word including the space, which is
 * ordinary conversational pace. It is a constant rather than a computation because nothing on this
 * side can do better, and it is named so it can be tuned from one place if Nova's voice settles
 * faster or slower.
 */
export const HEARD_CHARS_PER_SECOND = 15;

/** Server events that are read for bookkeeping but never relayed. */
const OUTPUT_AUDIO_STARTED = "output_audio_buffer.started";
const OUTPUT_AUDIO_STOPPED = "output_audio_buffer.stopped";
const OUTPUT_AUDIO_CLEARED = "output_audio_buffer.cleared";
const AUDIO_TRANSCRIPT_DELTA = "response.audio_transcript.delta";
const AUDIO_TRANSCRIPT_DONE = "response.audio_transcript.done";
const SPEECH_STARTED = "input_audio_buffer.speech_started";

/**
 * FLIP-274. Puts spoken turns on the server's record, and acts on what the server says back.
 *
 * WHY THIS EXISTS. On the typed path every turn is an HTTP request of ours, so recording it is a
 * line in SendMessage. Over WebRTC the audio goes browser-to-model and no request of ours is in the
 * turn at all, so nothing writes history: a ten-minute conversation leaves blank scrollback, an
 * empty EARLIER CONVERSATIONS block so typed Nova does not know what was just said aloud, no recent
 * chat for Insight generation, and an idle clock that never resets. This is mandatory, not
 * telemetry.
 *
 * WHY IT IS A SERVICE AND NOT PART OF THE CHAT COMPONENT. The same reason the transport is one
 * (FLIP-273): a call outlives the thing that started it. Minimising to the FAB or navigating away
 * tears the dialog down, and a relay owned by NovaCompanionChatComponent would stop recording at
 * exactly that moment - silently, which is the worst way for this particular thing to break.
 *
 * WHAT IT DOES NOT DO. It does not compose `function_call_output` and it does not classify
 * transcripts for safety; those are the tool-call ticket and FLIP-270. It surfaces what the server
 * returns for both and stops there.
 */
@Injectable({
  providedIn: "root",
})
export class NovaRealtimeRelayService implements OnDestroy {
  private readonly responsesSource = new Subject<NovaRealtimeRelayExchange>();
  /**
   * Every event relayed and what the endpoint said about it, in the order they were sent.
   *
   * The seam the tool-call ticket consumes: `nav`, `functionCallOutput` and `callId` are answered
   * here but acted on there, so that ticket does not have to re-do the relaying to get at them.
   *
   * FLIP-275 widened this from the bare response to the request-and-response pair, and made it
   * emit a null response for a relay that failed outright. Both are for the same caller: it needs
   * `functionName` and `arguments` to act on a call, and it must answer a function call EVEN WHEN
   * THE RELAY COULD NOT, or the model waits on an output that never comes for the rest of the
   * session. A failure that emits nothing cannot be recovered from downstream.
   */
  readonly responses$: Observable<NovaRealtimeRelayExchange> = this.responsesSource.asObservable();

  private readonly turnsSource = new Subject<NovaRelayedTurn>();
  /**
   * Turns now on the server's record.
   *
   * Exposed so the voice-mode ticket can render live scrollback without reaching into the event
   * stream and re-deriving what a turn is. Emitted for a duplicate as well as a fresh write:
   * both mean the turn is on file.
   */
  readonly turns$: Observable<NovaRelayedTurn> = this.turnsSource.asObservable();

  private readonly noticeSource = new Subject<string>();
  /** Messages meant for the user, in their own words. Currently only session termination. */
  readonly notice$: Observable<string> = this.noticeSource.asObservable();

  private readonly safetyEscalationSource = new Subject<void>();
  /**
   * The model raised a safety escalation on a spoken turn.
   *
   * Surfaced separately from `notice$` because crisis support is a different thing from a message:
   * it is shown alongside what was spoken rather than instead of it, and leaving it only heard is
   * not acceptable for this particular content.
   */
  readonly safetyEscalation$: Observable<void> = this.safetyEscalationSource.asObservable();

  private readonly clientHandledFunctionCallsSource = new Subject<NovaRealtimeEventRequest>();
  /**
   * B6. A call to one of `CLIENT_HANDLED_FUNCTIONS` - never posted to the backend, never appears
   * on `responses$`. `NovaWalkthroughService` is this stream's one intended subscriber; it
   * answers these itself via `sessionService.sendFunctionCallOutput`, which is what makes
   * "ordinary progression costs no round trip" (the Handoff's own phrase) true rather than
   * aspirational.
   */
  readonly clientHandledFunctionCalls$: Observable<NovaRealtimeEventRequest> =
    this.clientHandledFunctionCallsSource.asObservable();

  private subscription: Subscription | null = null;

  /**
   * The relay queue, as a promise chain.
   *
   * ORDERING IS PART OF THE CONTRACT, not a nicety. A truncate is what writes the assistant turn,
   * and the `response.done` that follows is refused by the item id they share - so if `response.done`
   * reached the server first it would write the FULL reply and the truncate would be the one
   * refused, storing words the user never heard. Retries make each relay take an unpredictable
   * amount of time, so the only way to keep the order is to send them one at a time.
   */
  private queue: Promise<void> = Promise.resolve();

  /** Accumulated `response.audio_transcript.delta` text, keyed by item id. */
  private transcripts = new Map<string, string>();

  /** The assistant item currently being spoken, if any. */
  private speakingItemId: string | null = null;

  /** When that item's audio started playing, as a wall-clock ms. */
  private speakingSince: number | null = null;

  /** Items already cut short, so a later `response.done` is reported as the truncated turn it is. */
  private truncated = new Set<string>();

  /**
   * Counts the user's turns: bumped each time they start speaking, including to interrupt. Stamped
   * on the calls the model makes so the server can hold a turn to one app-guidance call - a
   * follow-up response after a tool's output is still the same turn, because nobody spoke between.
   */
  private turnId = 0;

  constructor(
    private http: HttpClient,
    private sessionService: NovaRealtimeSessionService,
    @Inject(API_BASE_URL) private apiBaseUrl: string,
    private device: NovaDeviceService,
    private novaLog: NovaLogService
  ) {}

  /**
   * The user turn now under way: how many times they have started speaking. A step given to the
   * model in this turn is not followed by another until they speak again.
   */
  get currentTurnId(): number {
    return this.turnId;
  }

  /** True while the relay is listening. */
  get isRunning(): boolean {
    return this.subscription !== null;
  }

  /**
   * Starts relaying.
   *
   * Idempotent: calling it twice on one session would otherwise subscribe twice and relay every
   * event two times. The server would dedupe the writes, but it would also double the request rate
   * the migration exists to reduce.
   */
  start(): void {
    if (this.subscription) {
      return;
    }

    this.resetTurnState();
    this.subscription = this.sessionService.events$.subscribe((event) => this.onEvent(event));
  }

  /** Stops relaying and forgets the session's bookkeeping. In-flight relays are left to finish. */
  stop(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
      this.subscription = null;
    }

    this.resetTurnState();
  }

  ngOnDestroy(): void {
    this.stop();
  }

  /**
   * One event from the data channel.
   *
   * The bookkeeping branches come first and fall through to nothing: they are read so barge-in can
   * be answered, and are deliberately NOT relayed. Relaying audio deltas, VAD edges and rate-limit
   * updates would put a request on our server for every fragment of speech.
   */
  private onEvent(event: NovaRealtimeServerEvent): void {
    switch (event.type) {
      case OUTPUT_AUDIO_STARTED:
        this.onPlaybackStarted(event);
        return;

      case OUTPUT_AUDIO_STOPPED:
      case OUTPUT_AUDIO_CLEARED:
        this.speakingItemId = null;
        this.speakingSince = null;
        return;

      case AUDIO_TRANSCRIPT_DELTA:
        this.onTranscriptDelta(event);
        return;

      case AUDIO_TRANSCRIPT_DONE:
        this.onTranscriptDone(event);
        return;

      case SPEECH_STARTED:
        this.turnId += 1;
        this.onBargeIn();
        return;

      case NovaRealtimeRelayedEventTypes.InputTranscriptionCompleted:
        this.onUserTranscript(event);
        return;

      case NovaRealtimeRelayedEventTypes.ResponseDone:
        this.onResponseDone(event);
        return;

      case NovaRealtimeRelayedEventTypes.FunctionCallArgumentsDone:
        this.onFunctionCall(event);
        return;

      default:
        // Everything else - and there is a lot of it - is none of our business.
        return;
    }
  }

  private onPlaybackStarted(event: NovaRealtimeServerEvent): void {
    const itemId = this.stringField(event, "item_id");
    if (itemId) {
      this.speakingItemId = itemId;
    }
    this.speakingSince = Date.now();
  }

  private onTranscriptDelta(event: NovaRealtimeServerEvent): void {
    const itemId = this.stringField(event, "item_id");
    if (!itemId) {
      return;
    }

    this.transcripts.set(itemId, (this.transcripts.get(itemId) ?? "") + (this.stringField(event, "delta") ?? ""));

    // The fallback playback marker. `output_audio_buffer.started` is the accurate one, but it is a
    // WebRTC-specific event and a session that does not send it would otherwise leave every
    // barge-in with no elapsed time to work from - which would record an empty heard-text and lose
    // the turn entirely. Starting the clock at the first delta is early by a fraction of a second;
    // losing the turn is not recoverable.
    this.speakingItemId = this.speakingItemId ?? itemId;
    this.speakingSince = this.speakingSince ?? Date.now();
  }

  private onTranscriptDone(event: NovaRealtimeServerEvent): void {
    const itemId = this.stringField(event, "item_id");
    const transcript = this.stringField(event, "transcript");
    if (itemId && transcript !== null) {
      // The authoritative text for the item, replacing whatever the deltas accumulated to.
      this.transcripts.set(itemId, transcript);
    }
  }

  /**
   * The user spoke over Nova.
   *
   * Two things have to happen and the order matters. The truncate goes to OPENAI first, because
   * that is what actually stops the audio and trims the model's own context - a model that never
   * hears about the interruption carries on believing the user heard the whole reply. Then it is
   * relayed to us carrying the text actually heard, which is what writes the assistant turn.
   */
  private onBargeIn(): void {
    const itemId = this.speakingItemId;
    const since = this.speakingSince;
    if (!itemId || since === null || this.truncated.has(itemId)) {
      // No reply in flight, so this is the ordinary start of a user turn rather than an interruption.
      return;
    }

    const heardMs = Math.max(0, Date.now() - since);
    const heard = this.estimateHeardText(this.transcripts.get(itemId) ?? "", heardMs);

    this.truncated.add(itemId);
    this.speakingItemId = null;
    this.speakingSince = null;

    this.sessionService.sendClientEvent({
      type: NovaRealtimeRelayedEventTypes.ItemTruncate,
      item_id: itemId,
      content_index: 0,
      audio_end_ms: heardMs,
    });

    this.relay(
      {
        type: NovaRealtimeRelayedEventTypes.ItemTruncate,
        itemId,
        transcript: heard,
      },
      { role: "assistant", text: heard, itemId, truncated: true }
    );
  }

  /**
   * How much of the reply had actually been spoken when the user cut in.
   *
   * Capped by what has been transcribed so far, because the estimate is a rate and the transcript
   * is the ceiling: the user cannot have heard words the model had not generated. Trimmed back to
   * a word boundary so the stored turn reads as a sentence that stops, not one cut mid-word.
   */
  private estimateHeardText(full: string, heardMs: number): string {
    if (!full) {
      return "";
    }

    const budget = Math.floor((heardMs / 1000) * HEARD_CHARS_PER_SECOND);
    if (budget >= full.length) {
      return full;
    }
    if (budget <= 0) {
      return "";
    }

    const cut = full.slice(0, budget);
    if (full.charAt(budget) === " ") {
      // The estimate landed exactly on a word boundary, so the last word was fully spoken.
      return cut.trim();
    }

    const lastSpace = cut.lastIndexOf(" ");
    return (lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trim();
  }

  private onUserTranscript(event: NovaRealtimeServerEvent): void {
    const itemId = this.stringField(event, "item_id");
    const transcript = this.stringField(event, "transcript") ?? "";
    if (!itemId) {
      // The server needs an item id to make the write idempotent and answers 400 without one.
      return;
    }

    this.relay(
      {
        type: NovaRealtimeRelayedEventTypes.InputTranscriptionCompleted,
        itemId,
        transcript,
      },
      { role: "user", text: transcript, itemId, truncated: false }
    );
  }

  /**
   * Nova finished a reply.
   *
   * Relayed even when the item was truncated. The server refuses it by the item id the two share
   * and answers `duplicate: true`, which is a success - and going through the server rather than
   * suppressing it here means a truncate that failed to land still gets the assistant turn written,
   * in full, rather than not at all.
   */
  private onResponseDone(event: NovaRealtimeServerEvent): void {
    const message = this.assistantMessageOf(event);
    if (!message) {
      // A response that produced no spoken message - a tool call on its own, most often. Nothing
      // to record, and the function-call event carries what matters.
      return;
    }

    this.relay(
      {
        type: NovaRealtimeRelayedEventTypes.ResponseDone,
        itemId: message.itemId,
        transcript: message.transcript,
      },
      {
        role: "assistant",
        text: message.transcript,
        itemId: message.itemId,
        truncated: this.truncated.has(message.itemId),
      }
    );
  }

  /**
   * Digs the assistant's own words out of a `response.done`.
   *
   * The payload nests: `response.output[]` holds items, an assistant message item holds
   * `content[]`, and the spoken part of that carries `transcript`. Written defensively because a
   * shape that has moved should cost one unrecorded turn, not an exception that takes out the
   * subscription and every turn after it.
   */
  private assistantMessageOf(
    event: NovaRealtimeServerEvent
  ): { itemId: string; transcript: string } | null {
    const response = event["response"] as { output?: unknown } | undefined;
    const output =
      response && Array.isArray(response.output) ? (response.output as unknown[]) : [];

    for (const entry of output) {
      const item = entry as { id?: unknown; type?: unknown; role?: unknown; content?: unknown };
      if (item.type !== "message" || item.role !== "assistant" || typeof item.id !== "string") {
        continue;
      }

      const content = Array.isArray(item.content) ? (item.content as unknown[]) : [];
      for (const part of content) {
        const piece = part as { transcript?: unknown; text?: unknown };
        const text =
          typeof piece.transcript === "string"
            ? piece.transcript
            : typeof piece.text === "string"
            ? piece.text
            : null;
        if (text !== null) {
          return { itemId: item.id, transcript: text };
        }
      }

      // An assistant message with no readable content still names a turn that happened; the stored
      // transcript falls back to whatever the deltas gave us.
      return { itemId: item.id, transcript: this.transcripts.get(item.id) ?? "" };
    }

    return null;
  }

  /**
   * The model called a tool.
   *
   * B6 SPLITS THIS TWO WAYS. `navigate_to`, `raise_safety_escalation` and `begin_walkthrough` are
   * relayed so the server can resolve them; the answer comes back on `responses$` for the
   * tool-call ticket (and, for `begin_walkthrough`, `NovaWalkthroughService`) to act on. The other
   * five walkthrough tools have no server handler in v1 (see `CLIENT_HANDLED_FUNCTIONS`) and are
   * published on `clientHandledFunctionCalls$` instead - never posted to the backend at all. No
   * turn is recorded on either path, so nothing is published to `turns$`.
   */
  private onFunctionCall(event: NovaRealtimeServerEvent): void {
    const functionName = this.stringField(event, "name") ?? undefined;

    if (functionName && CLIENT_HANDLED_FUNCTIONS.has(functionName)) {
      this.clientHandledFunctionCallsSource.next({
        // sessionId/conversationId are meaningless for a call this service never sends anywhere -
        // filled in only because NovaRealtimeEventRequest requires them of every caller.
        sessionId: this.sessionService.currentSession?.sessionId ?? "",
        conversationId: this.sessionService.currentSession?.conversationId ?? "",
        type: NovaRealtimeRelayedEventTypes.FunctionCallArgumentsDone,
        itemId: this.stringField(event, "item_id") ?? undefined,
        functionName,
        arguments: this.stringField(event, "arguments") ?? undefined,
        callId: this.stringField(event, "call_id") ?? undefined,
      });
      return;
    }

    // The turn is read now, not when the queue gets to it: a call that waits behind a retry still
    // belongs to the turn it was made in.
    this.relay(
      {
        type: NovaRealtimeRelayedEventTypes.FunctionCallArgumentsDone,
        itemId: this.stringField(event, "item_id") ?? undefined,
        functionName,
        arguments: this.stringField(event, "arguments") ?? undefined,
        callId: this.stringField(event, "call_id") ?? undefined,
        turnId: this.turnId,
        // Read now, with the turn, so the directions are for the layout the user had when they asked.
        device: this.device.current() ?? undefined,
      },
      null
    );
  }

  /**
   * B6. Fetches one area's steps ahead of need - the "prefetch next area" leg of the per-area
   * design, and the one place a `begin_walkthrough`-shaped request goes out WITHOUT a model
   * having called anything.
   *
   * NOT `relay()`. This never publishes to `responses$`/`turns$` and is never answered with
   * `sendFunctionCallOutput` - there is no `call_id` waiting on it, because no tool call happened.
   * It is a plain data fetch that happens to reuse the same endpoint and DTO shape
   * `begin_walkthrough` already has, exactly as the architecture sheet's flow diagram frames the
   * area boundary crossing: "the only new server touch," not a new endpoint.
   *
   * Resolves to null on any failure - a missed prefetch costs a slower `begin_walkthrough` at the
   * boundary instead of an instant one, never a broken walkthrough, so this does not retry.
   */
  async prefetchWalkthroughArea(
    walkthroughId: string,
    area: string
  ): Promise<NovaRealtimeEventResponse | null> {
    const session = this.sessionService.currentSession;
    if (!session) {
      return null;
    }

    const body: NovaRealtimeEventRequest = {
      sessionId: session.sessionId,
      conversationId: session.conversationId,
      type: NovaRealtimeRelayedEventTypes.FunctionCallArgumentsDone,
      functionName: NovaRealtimeFunctions.BeginWalkthrough,
      arguments: JSON.stringify({ topic: walkthroughId, area }),
    };

    try {
      const response = await this.http
        .post<NovaRealtimeEventResponse>(`${this.apiBaseUrl}/api/Nova/RealtimeEvent`, body)
        .toPromise();
      return response ?? null;
    } catch {
      return null;
    }
  }

  /**
   * Queues one event for relay.
   *
   * `turn` is what to publish if the server accepts it, or null for an event that records nothing.
   * The session ids are read at SEND time rather than queue time so a relay that waited behind a
   * retry is still addressed to the session that is actually open.
   */
  private relay(
    event: Omit<NovaRealtimeEventRequest, "sessionId" | "conversationId">,
    turn: NovaRelayedTurn | null
  ): void {
    // Stamped now, not when the queue gets to it, so an event that waits behind a retry still
    // belongs to the turn it happened in. A function call already carries its own; every other
    // event gets the current one, which is how the server can tell a turn that used no tool.
    const stamped = { ...event, turnId: event.turnId ?? this.turnId };
    this.queue = this.queue
      .then(() => this.send(stamped, turn))
      .catch(() => undefined);
  }

  private async send(
    event: Omit<NovaRealtimeEventRequest, "sessionId" | "conversationId">,
    turn: NovaRelayedTurn | null
  ): Promise<void> {
    const session = this.sessionService.currentSession;
    if (!session) {
      // The call ended while this sat in the queue. There is no session for the server to key the
      // write on, and inventing one would have it refused as unknown.
      return;
    }

    const body: NovaRealtimeEventRequest = {
      ...event,
      sessionId: session.sessionId,
      conversationId: session.conversationId,
    };

    const response = await this.post(body);
    if (!response) {
      // Published even though there is nothing to report. A tool call that the relay could not
      // answer still has to be answered, and this is the only place that knows it failed.
      this.responsesSource.next({ request: body, response: null });
      return;
    }

    this.responsesSource.next({ request: body, response });

    if (response.safetyEscalationTriggered) {
      this.safetyEscalationSource.next();
    }

    if (response.sessionTerminated) {
      this.onSessionTerminated(response.message);
      return;
    }

    // A duplicate is a success. The server dedupes on item id precisely so a retry is safe, and
    // reporting one as a failure would have the caller show an error for a turn that is on file.
    if (turn && (response.recorded || response.duplicate)) {
      this.turnsSource.next(turn);
    }
  }

  /**
   * POSTs one event, retrying the failures that are worth retrying.
   *
   * The body is unchanged between attempts, so the `itemId` is reused and the server's dedupe holds
   * - a retry after a response that was written but never reached us costs a `duplicate: true`,
   * not a second copy of the turn.
   */
  private async post(body: NovaRealtimeEventRequest): Promise<NovaRealtimeEventResponse | null> {
    for (let attempt = 0; attempt < RELAY_ATTEMPTS; attempt++) {
      try {
        const response = await this.http
          .post<NovaRealtimeEventResponse>(`${this.apiBaseUrl}/api/Nova/RealtimeEvent`, body)
          .toPromise();
        return response ?? {};
      } catch (error) {
        if (this.isSessionGone(error)) {
          // 409. The controller is explicit that this is not "retry" and not "log out" - the
          // session has ended or was never this user's, and the client should stop relaying
          // against state that no longer exists.
          this.novaLog.warn("relay.session_gone", { eventType: body.type }, body.sessionId, "voice");
          this.onSessionTerminated(
            "This voice session has ended. You can start another one."
          );
          return null;
        }

        if (attempt === RELAY_ATTEMPTS - 1 || !this.isRetryable(error)) {
          // The turn is not recorded. Logged as an error because the user will find it missing.
          this.novaLog.error(
            "relay.post.failed",
            error,
            { eventType: body.type, attempts: attempt + 1 },
            body.sessionId,
            "voice"
          );
          return null;
        }
        this.novaLog.warn(
          "relay.post.retry",
          { eventType: body.type, attempt: attempt + 1, status: (error as any)?.status },
          body.sessionId,
          "voice"
        );

        await this.pause(RELAY_BACKOFF_MS[attempt] ?? 0);
      }
    }

    return null;
  }

  /**
   * Whether another attempt could plausibly do better.
   *
   * A 500 is the server saying the write failed, which it documents as the retryable blip - it
   * counts them and terminates the session itself once enough fail in a row. Status 0 is the
   * network. A 400 is not retryable by design: the controller's own comment says the client can fix
   * a malformed event and the server cannot, so re-sending the same body only burns the budget.
   */
  private isRetryable(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse)) {
      return false;
    }

    return error.status === 0 || error.status >= 500;
  }

  private isSessionGone(error: unknown): boolean {
    return error instanceof HttpErrorResponse && error.status === 409;
  }

  /**
   * The conversation is demonstrably going unrecorded, so it stops here.
   *
   * Continuing past this is what the ticket exists to prevent: a long conversation that left no
   * trace, discovered by the user tomorrow as an empty scrollback. Ending is the honest answer even
   * though it interrupts them.
   */
  private onSessionTerminated(message?: string): void {
    this.noticeSource.next(
      message || "Nova couldn't save this conversation, so the voice session has ended."
    );
    this.stop();
    this.sessionService.end("error").subscribe();
  }

  private pause(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private resetTurnState(): void {
    this.transcripts.clear();
    this.truncated.clear();
    this.speakingItemId = null;
    this.speakingSince = null;
    this.turnId = 0;
  }

  private stringField(event: NovaRealtimeServerEvent, key: string): string | null {
    const value = event[key];
    return typeof value === "string" ? value : null;
  }
}
